import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, finalize, map, Observable, tap } from 'rxjs';

import { QualityCheckItemTemplate } from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';

@Injectable({
  providedIn: 'root',
})
export class QualityCheckItemTemplateService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  getAll(loader: string): Observable<QualityCheckItemTemplate[]> {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/quality-check-item-templates`;
    return this.http.get<QualityCheckItemTemplate[]>(url).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  set(loader: string, dtos: QualityCheckItemTemplate[]): Observable<QualityCheckItemTemplate> {
    this.loadersService.show(loader);
    return this.http
      .put<QualityCheckItemTemplate>(this.baseUrl + '/quality-check-item-templates/batch-set', dtos)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  add(loader: string, dto: QualityCheckItemTemplate): Observable<QualityCheckItemTemplate> {
    this.loadersService.show(loader);
    return this.http
      .post<QualityCheckItemTemplate>(this.baseUrl + '/quality-check-item-templates', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, dto: QualityCheckItemTemplate): Observable<QualityCheckItemTemplate> {
    this.loadersService.show(loader);
    return this.http
      .put<QualityCheckItemTemplate>(this.baseUrl + '/quality-check-item-templates', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/quality-check-item-templates/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  import(loader: string, file: File): Observable<string[]> {
    this.loadersService.show(loader, 1);
    const formData = new FormData();
    formData.append('', file, file.name);
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });

    return this.http
      .post(`${this.baseUrl}/quality-check-item-templates/import`, formData, {
        reportProgress: true,
        observe: 'events',
        headers: headers,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<string[]> => event.type === HttpEventType.Response),
        map((response) => response.body as string[]),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  exportEmpty(loader: string, cultures: string[]) {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/quality-check-item-templates/export/empty`;
    return this.http
      .get(url, {
        responseType: 'blob',
        params: httpParams().array('cultures', cultures).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  export(loader: string) {
    this.loadersService.show(loader);

    const url = `${this.baseUrl}/quality-check-item-templates/export`;
    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
